import { Navigate, useParams, useSearchParams } from 'react-router-dom';

import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { getOpenChatConversationPath } from '@/modules/household/chat/householdChatDeepLink';
import { getValidationQuestionsPath } from '@/modules/validationQuestions/validationQuestions.paths';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
// eslint-disable-next-line luminary-rules/no-page-imports
import { ErrorCodes } from '@/pages/error/constants';
import { diagnostics } from '@/utils/diagnostics';
import { UnreachableError } from '@/utils/errors';

import { AI_SUGGESTION_MODAL_QUERY_PARAM } from '../constants';
import {
  getAISuggestionRegenRedirectPath,
  isAiSuggestionRegenScope,
} from '../utils/aiSuggestionRegen';
import { useAcknowledgeEmailNotification } from './hooks/useAcknowledgeEmailNotification';

enum NotificationRedirectPaths {
  // PublicError is a redirect to the public error page
  PublicError = 'public-error',
  // PresentationGenerated is a redirect to download the generated presentation
  // Format: /redirect/notifications/presentation-generated/:householdId/:documentId
  PresentationGenerated = 'presentation-generated',
  // ProposalGenerated is a redirect to download the generated proposal
  // Format: /redirect/notifications/proposal-generated/:householdId/:documentId
  ProposalGenerated = 'proposal-generated',
  // AISuggestion is a single- or multi-doc notification depending on if we're generating suggestions from a single document or multiple documents.
  // Format: /redirect/notifications/ai-suggestion/:householdId?document=do_123&document=do_456
  AISuggestion = 'ai-suggestion',
  // DocumentSummary is a single- or multi-doc notification depending on if we're generating a single document
  // or generating multiple summaries at the same time, through e.g. onboarding.
  // Format: /redirect/notifications/document-summary/:householdId?document=do_123&document=do_456
  DocumentSummary = 'document-summary',
  // EstateInsightsGenerated is a redirect to view generated insights
  // Format: /redirect/notifications/estate-insights-generated/:householdId
  EstateInsightsGenerated = 'estate-insights-generated',
  // Format: /redirect/notification/unmapped-integration-asset-categories
  UnmappedIntegrationAssetCategories = 'unmapped-integration-asset-categories',
  // ManualAiDp is a redirect to view the manual AI DP
  // Format: /redirect/notifications/manual-ai-dp/:householdId
  ManualAiDp = 'manual-ai-dp',
  // EstateSnapshotGenerated is a redirect to view the generated estate overview
  // Format: /redirect/notification/estate-snapshot-generated/:householdId/:presentationId (optional)
  EstateSnapshotGenerated = 'estate-snapshot-generated',
  // PresentationApprovalRequested opens the presentation that the user must attest or approve
  // Format: /redirect/notification/presentation-approval-requested/:householdId/:presentationId
  PresentationApprovalRequested = 'presentation-approval-requested',
  // DocumentSummaryExported is a redirect for document summary PDF/Word export notifications
  // Format: /redirect/notification/document-summary-exported/:householdId/:documentId?document_summary_id=...&generated_document_id=...
  DocumentSummaryExported = 'document-summary-exported',
  // MemoExported is a redirect for memo PDF/Word export notifications
  // Format: /redirect/notification/memo-exported/:householdId/:memoId?generated_document_id=...
  MemoExported = 'memo-exported',
  // MemoGenerated is a redirect for memo generation notifications
  // Format: /redirect/notification/memo-generated/:householdId/:memoId?
  MemoGenerated = 'memo-generated',
  // Format: /redirect/notification/form-extraction-completed/:householdId/:submissionId
  FormExtractionCompleted = 'form-extraction-completed',
  // AISuggestionRegen is a redirect for AI suggestion regeneration completion notifications
  // Format: /redirect/notification/ai-suggestion-regen/:householdId?scope=HOUSEHOLD&target=...&is_failure=true
  AISuggestionRegen = 'ai-suggestion-regen',
  // ValidationQuestions opens the household agent on the question conversation.
  // Format: /redirect/notification/validation-questions/:householdId/:conversationId?
  ValidationQuestions = 'validation-questions',
}

type NotificationPathParams = {
  householdId?: string;
  // the objectId is the ID of the object that the notification is for. this could be a document, a proposal, etc.
  objectId?: string;
};

export function getRequiredPathParam(
  pathParams: NotificationPathParams,
  paramName: keyof NotificationPathParams
) {
  const param = pathParams[paramName];
  if (!param) {
    throw new Error(`Required path param ${paramName} is missing`);
  }
  return param;
}

function getRequiredQueryParams(
  searchParams: URLSearchParams,
  paramName: string
) {
  const params = searchParams.getAll(paramName);
  if (params.length === 0) {
    throw new Error(`Required query param ${paramName} is missing`);
  }
  return params;
}

function getNormalizedRedirectPath(
  destination: NotificationRedirectPaths,
  pathParams: NotificationPathParams,
  searchParams: URLSearchParams,
  enhancedOnboarding: boolean
) {
  // This can be present for many (though not all) notification kinds
  const isFailure = searchParams.get('is_failure') === 'true';

  switch (destination) {
    case NotificationRedirectPaths.PublicError: {
      return getCompletePathFromRouteKey(ROUTE_KEYS.PUBLIC_ERROR, {
        errorCode: searchParams.get('errorCode') || 'UNKNOWN',
      });
    }
    case NotificationRedirectPaths.ProposalGenerated:
      const proposalId = getRequiredPathParam(pathParams, 'objectId');
      if (isFailure) {
        return getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_PROPOSALS,
          {
            householdId: getRequiredPathParam(pathParams, 'householdId'),
          },
          {
            failedProposalId: proposalId,
          }
        );
      }

      const documentId = searchParams.get('document_id');
      if (!documentId) {
        throw new Error('Document ID is required');
      }

      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_PROPOSALS,
        {
          householdId: getRequiredPathParam(pathParams, 'householdId'),
        },
        {
          downloadDocumentId: documentId,
        }
      );

    case NotificationRedirectPaths.PresentationGenerated: {
      const presentationId = getRequiredPathParam(pathParams, 'objectId');

      if (isFailure) {
        return getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATIONS,
          {
            householdId: getRequiredPathParam(pathParams, 'householdId'),
          },
          {
            failedPresentationId: presentationId,
          }
        );
      }

      const documentId = searchParams.get('document_id');
      if (!documentId) {
        throw new Error('Document ID is required');
      }

      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATIONS,
        {
          householdId: getRequiredPathParam(pathParams, 'householdId'),
        },
        {
          downloadDocumentId: documentId,
        }
      );
    }
    case NotificationRedirectPaths.AISuggestion: {
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_OVERVIEW,
        {
          householdId: getRequiredPathParam(pathParams, 'householdId'),
        },
        {
          [AI_SUGGESTION_MODAL_QUERY_PARAM]: true,
        }
      );
    }
    case NotificationRedirectPaths.ManualAiDp: {
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_ESTATE_MANAGE_DISPOSITIONS,
        {
          householdId: getRequiredPathParam(pathParams, 'householdId'),
        }
      );
    }
    case NotificationRedirectPaths.DocumentSummary: {
      const documentIds = getRequiredQueryParams(searchParams, 'documents');

      if (documentIds.length === 1) {
        const documentId = documentIds[0];
        if (!documentId) {
          throw new Error('Document ID is required');
        }

        return getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENT_DETAILS,
          {
            householdId: getRequiredPathParam(pathParams, 'householdId'),
            documentId,
          }
        );
      }

      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS,
        {
          householdId: getRequiredPathParam(pathParams, 'householdId'),
        }
      );
    }
    case NotificationRedirectPaths.EstateInsightsGenerated: {
      return getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_INSIGHTS, {
        householdId: getRequiredPathParam(pathParams, 'householdId'),
      });
    }
    case NotificationRedirectPaths.UnmappedIntegrationAssetCategories: {
      return getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_CATEGORIES, {});
    }
    case NotificationRedirectPaths.EstateSnapshotGenerated: {
      const presentationId = pathParams.objectId;
      if (presentationId) {
        return getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATION_V2,
          {
            householdId: getRequiredPathParam(pathParams, 'householdId'),
            presentationId,
          }
        );
      }
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATIONS,
        {
          householdId: getRequiredPathParam(pathParams, 'householdId'),
        }
      );
    }
    case NotificationRedirectPaths.PresentationApprovalRequested: {
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATION_V2,
        {
          householdId: getRequiredPathParam(pathParams, 'householdId'),
          presentationId: getRequiredPathParam(pathParams, 'objectId'),
        }
      );
    }
    case NotificationRedirectPaths.DocumentSummaryExported: {
      const householdId = getRequiredPathParam(pathParams, 'householdId');
      const documentId = getRequiredPathParam(pathParams, 'objectId');

      if (isFailure) {
        // On failure, navigate to the document details page (document summary details)
        return getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENT_DETAILS,
          {
            householdId,
            documentId,
          }
        );
      }

      const generatedDocumentId = searchParams.get('generated_document_id');
      if (!generatedDocumentId) {
        throw new Error('Generated document ID is required');
      }

      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS,
        {
          householdId,
        },
        {
          downloadDocumentId: generatedDocumentId,
        }
      );
    }
    case NotificationRedirectPaths.MemoExported: {
      const householdId = getRequiredPathParam(pathParams, 'householdId');
      const memoId = getRequiredPathParam(pathParams, 'objectId');

      if (isFailure) {
        // On failure, navigate to the memo details page
        return getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_MEMO_DETAILS,
          {
            householdId,
            memoId,
          }
        );
      }

      const generatedDocumentId = searchParams.get('generated_document_id');
      if (!generatedDocumentId) {
        throw new Error('Generated document ID is required');
      }

      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS,
        {
          householdId,
        },
        {
          downloadDocumentId: generatedDocumentId,
        }
      );
    }
    case NotificationRedirectPaths.MemoGenerated: {
      const householdId = getRequiredPathParam(pathParams, 'householdId');
      const memoId = pathParams.objectId;

      if (isFailure || !memoId) {
        // On failure or missing memo ID, navigate to household overview
        return getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_OVERVIEW,
          {
            householdId,
          }
        );
      }

      // On success, navigate to the memo details page
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_MEMO_DETAILS,
        {
          householdId,
          memoId,
        }
      );
    }
    case NotificationRedirectPaths.FormExtractionCompleted:
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_FORM_SUBMISSION_RESULT,
        {
          householdId: getRequiredPathParam(pathParams, 'householdId'),
          submissionId: getRequiredPathParam(pathParams, 'objectId'),
        }
      );
    case NotificationRedirectPaths.AISuggestionRegen: {
      const scope = searchParams.get('scope');
      if (!isAiSuggestionRegenScope(scope)) {
        throw new Error(
          `Invalid or missing scope for AI suggestion regen redirect: ${scope}`
        );
      }
      return getAISuggestionRegenRedirectPath({
        scope,
        householdId: getRequiredPathParam(pathParams, 'householdId'),
        targetId: searchParams.get('target') ?? undefined,
      });
    }
    case NotificationRedirectPaths.ValidationQuestions: {
      const householdId = getRequiredPathParam(pathParams, 'householdId');
      if (enhancedOnboarding) return getValidationQuestionsPath(householdId);
      if (pathParams.objectId) {
        return getOpenChatConversationPath(householdId, pathParams.objectId);
      }

      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_OVERVIEW,
        { householdId }
      );
    }
    default: {
      throw new UnreachableError({
        message: `Unhandled notification redirect path: ${destination}`,
        case: destination,
      });
    }
  }
}

// getValidatedRedirectPath is a helper function to validate that the
// notification redirect path is valid.
// if it's not, we redirect to the public error page.
function getValidatedRedirectPath(
  notificationKind?: string
): NotificationRedirectPaths {
  const redirectPathValues = Object.values(
    NotificationRedirectPaths
  ) as string[];
  if (!notificationKind || !redirectPathValues.includes(notificationKind)) {
    diagnostics.error(
      'Invalid notification redirect path',
      new Error(
        `No specified redirect path found for notificationKind ${notificationKind}; redirecting to the public error page`
      )
    );

    return NotificationRedirectPaths.PublicError;
  }

  return notificationKind as NotificationRedirectPaths;
}

// NotificationRedirect is a component that redirects to the appropriate page when a notification is clicked
// from an email. it's a separate path from FUNCTIONAL_REDIRECT because there are enough different paths and parameters
// being passed that it's simpler to have a separate path for it.
export function NotificationRedirect() {
  useAcknowledgeEmailNotification();
  const enhancedOnboarding = useFeatureFlag(
    'enhanced_household_onboarding_flow'
  );
  const { notificationKind, householdId, objectId } = useParams<{
    notificationKind: string;
    householdId?: string;
    objectId?: string;
  }>();

  const [searchParams] = useSearchParams();

  const pathParams = { householdId, objectId };

  let redirectPath: string;
  try {
    redirectPath = getNormalizedRedirectPath(
      getValidatedRedirectPath(notificationKind),
      pathParams,
      searchParams,
      enhancedOnboarding
    );
  } catch (error) {
    diagnostics.error(
      'Invalid notification redirect path',
      error instanceof Error ? error : new Error(String(error))
    );

    redirectPath = getCompletePathFromRouteKey(ROUTE_KEYS.PUBLIC_ERROR, {
      errorCode: ErrorCodes.UNKNOWN,
    });
  }

  return <Navigate to={redirectPath} replace />;
}
